import React from "react";
import { Helmet } from "react-helmet";
import PropTypes from "prop-types";
import { StaticQuery, graphql } from "gatsby";
import HelmetWithGoogle from "./HelmetWithGoogle";
import HelmetWithOneTrust from "./HelmetWithOneTrust";
import HelmetWithWebSights from "./HelmetWithWebSights";

// fashions up an SEO-friendly title for articles. Should return a string with format: "Article Title | Mode"
const createArticleSeoTitle = title => `${title} | Mode`;

/* eslint-disable no-use-before-define */
const handleImagePath = (article, url, image, defaultImage) => {
  if (article && image) {
    return image;
  }
  if (image) {
    return `${url}${image}`;
  }
  return `${url}${defaultImage}`;
};

// construcst path to be used for canonical and og urls. Ensures all urls have a single trailing slash.
const constructSeoUrl = (siteUrl, pagePath) => {
  let seoUrl = `${siteUrl}${pagePath || ""}`;
  if (seoUrl.charAt(seoUrl.length - 1) !== "/") {
    seoUrl = `${seoUrl}/`;
  }
  return seoUrl;
};

const SEO = ({ title, description, image, pathname, objectType }) => (
  <StaticQuery
    query={query}
    render={({
      site: {
        siteMetadata: {
          defaultTitle,
          defaultDescription,
          url,
          defaultImage,
          favicon,
          twitterUsername,
          facebookPublisher,
          facebookAdminIds
        }
      }
    }) => {
      const seo = {
        title:
          objectType === "article"
            ? createArticleSeoTitle(title || defaultTitle)
            : title || defaultTitle,
        description: description || defaultDescription,
        image: handleImagePath(
          objectType === "article",
          url,
          image,
          defaultImage
        ),
        url: constructSeoUrl(url, pathname),
        type: objectType
      };

      return (
        <>
          <Helmet
            title={seo.title}
            script={[
              {
                type: "text/javascript",
                innerHTML: `
                  !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on"];analytics.factory=function(t){return function(){var e=Array.prototype.slice.call(arguments);e.unshift(t);analytics.push(e);return analytics}};for(var t=0;t<analytics.methods.length;t++){var e=analytics.methods[t];analytics[e]=analytics.factory(e)}analytics.load=function(t,e){var n=document.createElement("script");n.type="text/javascript";n.async=!0;n.src="https://cdn.segment.com/analytics.js/v1/"+t+"/analytics.min.js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(n,a);analytics._loadOptions=e};analytics.SNIPPET_VERSION="4.1.0";
                    analytics.load("${process.env.GATSBY_SEGMENT_PRODUCTION_ID}");
                    analytics.page({}, {
                      integrations: {
                        'Intercom': false,
                        'Marketo': false
                       }
                     });
                   }}();
                 `
              }
            ]}
          >
            <link rel="shortcut icon" type="image/png" href={favicon} />
            <link
              rel="shortcut icon"
              type="image/png"
              href={`https://mode.com/blog${favicon}`}
            />

            <meta name="description" content={seo.description} />
            <link rel="canonical" href={seo.url} />
            <meta name="image" content={seo.image} />

            {seo.type && <meta property="og:type" content={seo.type} />}

            {seo.title && <meta property="og:title" content={seo.title} />}

            {seo.description && (
              <meta property="og:description" content={seo.description} />
            )}

            {seo.image && <meta property="og:image" content={seo.image} />}

            <meta name="twitter:card" content="summary_large_image" />

            {twitterUsername && (
              <meta name="twitter:creator" content={twitterUsername} />
            )}

            {seo.title && <meta name="twitter:title" content={seo.title} />}

            {seo.description && (
              <meta name="twitter:description" content={seo.description} />
            )}

            {seo.image && <meta name="twitter:image" content={seo.image} />}
          </Helmet>
          <HelmetWithGoogle />
          <HelmetWithOneTrust />
          <HelmetWithWebSights />
        </>
      );
    }}
  />
);

export default SEO;

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  pathname: PropTypes.string,
  objectType: PropTypes.string
};

SEO.defaultProps = {
  title: null,
  description: null,
  image: null,
  pathname: null,
  objectType: "website"
};

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        defaultTitle: title
        defaultDescription: description
        url
        defaultImage: image
        favicon
        twitterUsername
        facebookPublisher
        facebookAdminIds
      }
    }
  }
`;
